import React, { createContext, useContext, useReducer } from 'react'
import {
  IPotentialChartParams,
  useSavingsToolkitApi,
} from '../api/savings-toolkit-api'
import { StkGrowthChartData, StkPotentialChartData } from '../types'
import { stkServerErrors } from '../utils/tagging/stkTagging'
import { API_STATUS } from '../utils/constants'

// Available to context consumers
export interface StkChartsProps {
  potential: StkPotentialChartData | null
  growth: StkGrowthChartData | null
  hasPotentialError: boolean
  isPotentialLoading: boolean
  isPotentialUpdating: boolean
  hasGrowthError: boolean
  isGrowthLoading: boolean
  getGrowthChart: () => Promise<void>
  getPotentialChart: (params: IPotentialChartParams) => Promise<void>
  updatePotentialChart: (params: IPotentialChartParams) => Promise<void>
}

interface IChartsState {
  potential: {
    data: StkPotentialChartData | null
    status: API_STATUS
  }
  growth: {
    data: StkGrowthChartData | null
    status: API_STATUS
  }
}

type IReducerAction =
  | {
      type: 'SET_POTENTIAL'
      potential: StkPotentialChartData | null
    }
  | {
      type: 'SET_GROWTH'
      growth: StkGrowthChartData | null
    }
  | {
      type: 'SET_GROWTH_STATUS'
      status: API_STATUS
    }
  | {
      type: 'SET_POTENTIAL_STATUS'
      status: API_STATUS
    }

export const StkChartsContext = createContext({} as StkChartsProps)

export const chartsReducer = (
  state: IChartsState,
  action: IReducerAction,
): IChartsState => {
  switch (action.type) {
    case 'SET_POTENTIAL_STATUS':
      return {
        ...state,
        potential: { ...state.potential, status: action.status },
      }
    case 'SET_GROWTH_STATUS':
      return {
        ...state,
        growth: { ...state.growth, status: action.status },
      }
    case 'SET_POTENTIAL':
      return {
        ...state,
        potential: { ...state.potential, data: action.potential },
      }
    case 'SET_GROWTH':
      return {
        ...state,
        growth: { ...state.growth, data: action.growth },
      }
    default: {
      return state
    }
  }
}

export const StkChartsProvider: React.FC = ({ children }) => {
  const api = useSavingsToolkitApi()

  const [state, dispatch] = useReducer(chartsReducer, {
    potential: {
      data: null,
      status: API_STATUS.IDLE,
    },
    growth: {
      data: null,
      status: API_STATUS.IDLE,
    },
  })

  const getGrowthChart = async (): Promise<void> => {
    dispatch({ type: 'SET_GROWTH_STATUS', status: API_STATUS.PENDING })
    try {
      const growth = await api.getStkGrowthChart()
      dispatch({ type: 'SET_GROWTH', growth })
      dispatch({ type: 'SET_GROWTH_STATUS', status: API_STATUS.RESOLVED })
    } catch (e) {
      console.log(e)
      stkServerErrors.getAnalyzeServerError(e as Error)
      dispatch({ type: 'SET_GROWTH_STATUS', status: API_STATUS.REJECTED })
    }
  }

  const getPotentialChart = async (
    params: IPotentialChartParams,
  ): Promise<void> => {
    dispatch({ type: 'SET_POTENTIAL_STATUS', status: API_STATUS.PENDING })
    try {
      const potential = await api.getStkPotentialChart(params)
      dispatch({ type: 'SET_POTENTIAL', potential })
      dispatch({ type: 'SET_POTENTIAL_STATUS', status: API_STATUS.RESOLVED })
    } catch (e) {
      console.log(e)
      /* istanbul ignore next */
      stkServerErrors.getAnalyzeServerError(e as Error)
      dispatch({ type: 'SET_POTENTIAL_STATUS', status: API_STATUS.REJECTED })
    }
  }

  // called when given new recurring transfer amount from form
  const updatePotentialChart = async (
    params: IPotentialChartParams,
  ): Promise<void> => {
    dispatch({ type: 'SET_POTENTIAL_STATUS', status: API_STATUS.UPDATING })
    try {
      const potential = await api.getStkPotentialChart(params)
      dispatch({ type: 'SET_POTENTIAL', potential })
      dispatch({ type: 'SET_POTENTIAL_STATUS', status: API_STATUS.RESOLVED })
    } catch (e) {
      /* istanbul ignore next */
      stkServerErrors.updateChartServerError(e as Error)
      /* istanbul ignore next */
      console.log(e)
      /* istanbul ignore next */
      dispatch({ type: 'SET_POTENTIAL_STATUS', status: API_STATUS.REJECTED })
    }
  }

  return (
    <StkChartsContext.Provider
      value={{
        // General data
        potential: state.potential.data,
        growth: state.growth.data,

        // Loading/error states
        hasPotentialError: state.potential.status === API_STATUS.REJECTED,
        isPotentialLoading: state.potential.status === API_STATUS.PENDING,
        isPotentialUpdating: state.potential.status === API_STATUS.UPDATING,
        hasGrowthError: state.growth.status === API_STATUS.REJECTED,
        isGrowthLoading: state.growth.status === API_STATUS.PENDING,

        // Fetching functions
        getPotentialChart,
        updatePotentialChart,
        getGrowthChart,
      }}
    >
      {children}
    </StkChartsContext.Provider>
  )
}

export const useStkCharts = (): StkChartsProps => useContext(StkChartsContext)
